import { useModalState } from "@clipboard-health/ui-react";
import { AppV2Routes } from "@src/appV2";
import { cbhQueryClient } from "@src/appV2/api/queryClient";
import { getExistingSessionEmail } from "@src/appV2/Auth/cognito/cognitoAuth";
import { isFirebaseSessionFallbackEnabled } from "@src/appV2/Auth/cognito/firebaseSessionFallbackState";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Login } from "@src/appV2/redesign/Login/Login";
import { Platform } from "@src/mobile/utils/platform";
import { QueryClientProvider } from "@tanstack/react-query";
import { HomeHealthProtectedCasesPage } from "@src/appV2/ExperimentalHomeHealth/Cases/ProtectedCasesPage";
import { HomeHealthProtectedVisitVerificationPage } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/ProtectedVisitVerificationPage";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { useShouldRedirectToDailyView } from "@src/appV2/redesign/DailyView/hooks/useShouldRedirectToDailyView";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import firebase from "firebase/app";
import { Suspense, useEffect, useRef, useState } from "react";
import { Redirect, Route, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { type SessionType } from "@src/modules/interface";
import { useInitializeLaunchDarkly } from "@src/hooks/useInitializeLaunchDarkly";

import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { AdminMenuPrivilegesProvider } from "./Shared/Navigation/privilegesProviders/AdminMenuPrivilegesProvider";
import { WorkplaceMenuPrivilegesProvider } from "./Shared/Navigation/privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { ConnectedOmnisearch } from "@src/containers/app/omnisearch";
import { useReloadPageOnUserSwitch } from "@src/containers/app/hooks/useReloadPageOnUserSwitch";
import { AppLayoutDesktop } from "./Desktop/AppLayoutDesktop";
import { type Theme, useMediaQuery } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { AppLayoutMobile } from "./Mobile/AppLayoutMobile";
import { WorkerMenuPrivilegesProvider } from "./Shared/Navigation/privilegesProviders/WorkerMenuPrivilegesProvider";
import { FloatingTabsProvider } from "@src/appV2/redesign/AppLayout/Mobile/FloatingTabsContext";
import { LogoutConfirmationDialog } from "./Shared/LogoutConfirmationDialog";
import { AppLayoutTablet } from "./Tablet/Navigation/AppLayoutTablet";
import { BrazeInitializedProvider } from "@src/appV2/redesign/AppLayout/Shared/BrazeContext";
import { ModalsContainerProvider } from "@src/appV2/redesign/components/useModalsContainerContext";

interface NavigationMenuContainerProps {
  admin: boolean;
  isAdmin: boolean;
  isAgent: boolean;
  isFacility: boolean;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;
  get: () => void;
  getAdminProfile: () => void;
  loadingAdminProfile: boolean;
  initFirebaseSession: () => Promise<void>;
  initCognitoSession: () => Promise<boolean>;
  getLocations: () => void;
  getEmployees: () => void;
}

const AdminAppLogin = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.AdminAppLogin,
    }))
);

const FirebaseLoginVerify = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.FirebaseLoginVerify,
    }))
);

/**
 * Below this width the navigation collapses to icon-only mode.
 * At or above this width, the full text navigation sidebar is shown.
 */
const NAV_EXPAND_THRESHOLD_PX = 1500;

export function NavigationMenuContainer(props: NavigationMenuContainerProps): JSX.Element {
  return (
    <BerlinThemeProvider>
      <NavigationMenuContainerContent {...props} />
    </BerlinThemeProvider>
  );
}

function NavigationMenuContainerContent(props: NavigationMenuContainerProps): JSX.Element {
  const {
    admin,
    isAdmin,
    isAgent: isWorker,
    isFacility: isWorkplace,
    session,
    get,
    getAdminProfile,
    loadingAdminProfile,
    initFirebaseSession,
    initCognitoSession,
    getLocations,
    getEmployees,
  } = props;

  const logoutDialog = useModalState();

  const [loading, setLoading] = useState(true);
  const { ready, profile, user } = useSelector((state: SessionType) => state.session || {});
  const { access } = user ?? {};
  const isAuthorized = isAdmin || isWorker || isWorkplace;

  const history = useHistory();
  const location = useLocation();

  const isMobile = useIsMobile();
  const isNavCollapsed = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("md", NAV_EXPAND_THRESHOLD_PX)
  );

  // Home Health check for start page redirect
  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const shouldRedirectToDailyView = useShouldRedirectToDailyView();

  // init launch darkly
  useInitializeLaunchDarkly({
    profile,
    isAdmin,
    adminUserId: session.adminUserId,
    workplaceId: isWorkplace ? session.userId : undefined,
  });

  // Reload the page when the user switches on routes that have stale data bugs.
  // This matches the legacy AppLayout behavior (appLayout.tsx line 827).
  useReloadPageOnUserSwitch();

  // Initialize admin profile
  useEffect(() => {
    if (loadingAdminProfile) {
      getAdminProfile();
    }
  }, [loadingAdminProfile, getAdminProfile]);

  // Track when we transition from a public auth route (e.g. /v2/email-login-link → /)
  // so that session init can re-run to hydrate newly stored Cognito tokens.
  const [sessionInitCounter, setSessionInitCounter] = useState(0);
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    const wasAuthRoute = prevPathnameRef.current.startsWith(routes.appV2);
    prevPathnameRef.current = location.pathname;

    if (wasAuthRoute && !location.pathname.startsWith(routes.appV2)) {
      setSessionInitCounter((count) => count + 1);
    }
  }, [location.pathname]);

  // Initialize session: Cognito is source of truth, Firebase is fallback when enabled.
  // Runs on mount and when sessionInitCounter increments (i.e. after an auth route
  // transition), but NOT on every pathname change.
  useEffect(() => {
    async function load() {
      setLoading(true);
      const cognitoSessionRestored = await initCognitoSession();
      if (!cognitoSessionRestored && isFirebaseSessionFallbackEnabled()) {
        logEvent(APP_V2_APP_EVENTS.COGNITO_SESSION_INIT_FIREBASE_FALLBACK);
        await initFirebaseSession();
      }
      setLoading(false);
    }
    void load();
  }, [initCognitoSession, initFirebaseSession, sessionInitCounter]);

  // Fetch session data and locations
  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (!isAuthorized) {
      return;
    }
    get();
    getLocations();

    if (session?.admin) {
      getEmployees();
    }
  }, [isAuthorized, get, getLocations, getEmployees, loading, ready, session?.admin]);

  // Redirect to the login page if the user is not authenticated.
  // When Firebase fallback is enabled, use Firebase auth state as a safety net
  // for legacy sessions. When disabled, skip the listener entirely since
  // Cognito handles session state through Redux.
  useEffect(() => {
    if (!isFirebaseSessionFallbackEnabled()) {
      return;
    }

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    return firebase.auth().onAuthStateChanged(async (user) => {
      // Use React Router's location.pathname (which strips the basename)
      // instead of window.location.pathname (which includes it). On PR
      // previews the browser path is /pr-NNN/v2/... but routes.appV2 is
      // /v2, so the raw browser path never matches.
      const isPublicAuthRoute =
        location.pathname.includes("login/verify") || location.pathname.startsWith(routes.appV2);

      if (!user && !isPublicAuthRoute) {
        try {
          const cognitoEmail = await getExistingSessionEmail();
          if (!cognitoEmail) {
            history.replace("/login");
          }
        } catch {
          // If the Cognito check fails, preserve the current state rather than
          // redirecting — the user may still have a valid session.
        }
      }
    });
  }, [history, location.pathname]);

  function renderStartPage() {
    if (isAdmin) {
      return <Redirect to={routes.adminAccountsAgents} />;
    }

    if (isWorker) {
      return <Redirect to={routes.agentDashboard} />;
    }

    if (isWorkplace) {
      if (isHomeHealthEnabled) {
        return <Redirect to={routes.homeHealthCases} />;
      }
      if (shouldRedirectToDailyView) {
        return <Redirect to={routes.schedule} />;
      }
      return <Redirect to={routes.facilityDashboard} />;
    }

    return null;
  }

  function renderAppLayout() {
    if (isMobile) {
      return (
        <AppLayoutMobile
          isAdmin={isAdmin}
          isWorker={isWorker}
          admin={admin}
          isWorkplace={isWorkplace}
          userName={profile.name}
          userId={profile.userId}
          logoutConfirm={logoutDialog.openModal}
          renderStartPage={renderStartPage}
        />
      );
    }

    if (isNavCollapsed) {
      return (
        <AppLayoutTablet
          isAdmin={isAdmin}
          isWorker={isWorker}
          admin={admin}
          isWorkplace={isWorkplace}
          userName={profile.name}
          userId={profile.userId}
          logoutConfirm={logoutDialog.openModal}
          renderStartPage={renderStartPage}
        />
      );
    }

    return (
      <AppLayoutDesktop
        admin={admin}
        isAdmin={isAdmin}
        isWorker={isWorker}
        isWorkplace={isWorkplace}
        userName={profile.name}
        userId={profile.userId}
        logoutConfirm={logoutDialog.openModal}
        renderStartPage={renderStartPage}
      />
    );
  }

  // Early return for routes that must render regardless of auth state.
  // These routes handle their own auth redirects (e.g. to /enter?requestedUrl=...)
  // and must bypass NavigationMenuContainer's auth guards which redirect to /login.
  const isPreAuthRoute =
    location.pathname === routes.login ||
    location.pathname === routes.loginVerify ||
    (location.pathname.startsWith(routes.appV2) && !isAuthorized) ||
    (location.pathname.startsWith("/home-health") && !isAuthorized);

  if (isPreAuthRoute) {
    return (
      <Suspense fallback={<div style={{ height: "100vh" }} />}>
        <div
          style={{
            height: "100vh",
            paddingTop: "env(safe-area-inset-top, 0px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            boxSizing: "border-box",
          }}
        >
          <Route exact path={routes.login} component={Platform.isMobile ? Login : AdminAppLogin} />
          <Route exact path={routes.loginVerify} component={FirebaseLoginVerify} />
          <Route path={routes.appV2}>
            <QueryClientProvider client={cbhQueryClient}>
              <AppV2Routes />
            </QueryClientProvider>
          </Route>
          <Route path={routes.homeHealthCases} component={HomeHealthProtectedCasesPage} />
          <Route
            path={routes.homeHealthVisitVerification}
            component={HomeHealthProtectedVisitVerificationPage}
          />
        </div>
      </Suspense>
    );
  }

  // Guard to prevent rendering while session is initializing
  // This matches the old AppLayout logic (line 918 in appLayout.tsx)
  // Prevents API calls from firing before auth token and profile data are ready
  //
  // Important: ready becomes true BEFORE fetchSession() completes loading the full profile
  // We need to wait for profile.tmz to exist as it's required by API calls in AgentDashboard
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const hasRequiredProfileData = profile && profile.userId;

  if ((loading || !ready) && session.userId) {
    return <></>;
  }

  // If user is logged out (no profile data and no session loading), redirect to login.
  // This prevents users from getting stuck on a blank page after logout.
  // Skip the redirect when Cognito tokens exist in localStorage — the session init
  // effect is about to hydrate them into Redux (e.g. after AuthRedirect stores tokens
  // and navigates here).
  if (!hasRequiredProfileData && !loading) {
    const hasCognitoTokens = Object.keys(localStorage).some((key) =>
      key.startsWith("CognitoIdentityServiceProvider")
    );
    const hasFirebaseUser =
      isFirebaseSessionFallbackEnabled() && Boolean(firebase.auth().currentUser);
    if (!hasCognitoTokens && !hasFirebaseUser) {
      return <Redirect to="/login" />;
    }
  }

  // Additional guard: ensure profile data is fully loaded before rendering
  // AgentDashboard needs profile.tmz, profile.geoLocation, profile.preference, etc.
  if (!hasRequiredProfileData) {
    return <></>;
  }

  // Guard for workplace users: wait for roles (access) to be loaded
  // This prevents a redirect loop where PrivateRoute redirects to /enter because
  // isAuthorized is false (roles not loaded), and /enter redirects back to /facility/dashboard
  // This matches the guard in legacy appLayout.tsx (line 975-977)
  if (isWorkplace && !admin && access === undefined) {
    return <></>;
  }

  return (
    <ModalsContainerProvider>
      <BrazeInitializedProvider isAdminSession={admin} isWorkplace={isWorkplace}>
        <AdminMenuPrivilegesProvider>
          <WorkplaceMenuPrivilegesProvider>
            <WorkerMenuPrivilegesProvider>
              <FloatingTabsProvider>
                <ConnectedOmnisearch />
                {renderAppLayout()}
                <LogoutConfirmationDialog modalState={logoutDialog} />
              </FloatingTabsProvider>
            </WorkerMenuPrivilegesProvider>
          </WorkplaceMenuPrivilegesProvider>
        </AdminMenuPrivilegesProvider>
      </BrazeInitializedProvider>
    </ModalsContainerProvider>
  );
}
